import { DestroyRef, Injectable, inject, signal } from '@angular/core';

/**
 * L'état de la connexion, tel que le navigateur le connaît (`navigator.onLine` + événements
 * `online` / `offline`).
 *
 * <p>Un seul signal pour tout le SI : un envoi de pièces qui perd le réseau doit le dire à l'agent,
 * se mettre en pause, et reprendre seul au retour — sans que chaque écran réécoute la fenêtre.
 *
 * <p>⚠️ `onLine === true` ne garantit PAS que l'API répond (proxy coupé, Wi-Fi sans Internet) : il
 * dit seulement que le poste n'est pas débranché. Une requête qui tombe en statut 0 est donc, elle
 * aussi, traitée comme une coupure par ses appelants ({@link estCoupureReseau}).
 */
@Injectable({ providedIn: 'root' })
export class NetworkStatusService {
  readonly online = signal<boolean>(typeof navigator === 'undefined' ? true : navigator.onLine);

  constructor() {
    if (typeof window === 'undefined') {
      return;
    }
    const enLigne = (): void => this.online.set(true);
    const horsLigne = (): void => this.online.set(false);
    window.addEventListener('online', enLigne);
    window.addEventListener('offline', horsLigne);
    inject(DestroyRef).onDestroy(() => {
      window.removeEventListener('online', enLigne);
      window.removeEventListener('offline', horsLigne);
    });
  }
}

/**
 * Une erreur HTTP est-elle une coupure (le serveur n'a jamais répondu) plutôt qu'un refus ?
 * Statut 0 = la requête n'a pas abouti : réseau perdu, serveur injoignable, requête avortée.
 */
export function estCoupureReseau(e: unknown): boolean {
  return !!e && typeof e === 'object' && 'status' in e && (e as { status: unknown }).status === 0;
}
